import React from 'react';
import { useCreateCompanyMutation, useUpdateCompanyMutation } from '../Service/companiesApi';

import { Company } from '../Types/companyTypes';
import { companyValues } from '../initialValues';
import { companyFields } from '../fields';
import { CompaniesRoutes } from '../../../Router/path';
import EntityFormLayout from '../../Shared/Components/EntityFormLayout/EntityFormLayout';
import InfoIcon from '@mui/icons-material/Info';
import { FormRenderGroupTypes } from '../../Shared/Components/FormRenderer/FormRenderGroup.types';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CompanyAccountsBankTable from '../Componets/CompanyAccountsBankTable';
import { useUploadCompanyImagesMutation } from '../Service/extendedCompaniesApi';
import { useGetBankAccountsForCompanyQuery } from '../../BankAccounts';
import { useEntityForm } from '../../Shared/Hooks/useEntityForm';
import AccordionCard from '../../Shared/Components/AccordionCard/AccordionCard';
import ImageIcon from '@mui/icons-material/Image';
import UploadImagesForm from '../../Shared/Components/UploadImagesForm/UploadImagesForm';
import { Typography } from '@mui/material';

const CreateCompany = () => {
  const [createdCompanyId, setCreatedCompanyId] = React.useState<number | null>(null);

  const [createCompany, statusCreate] = useCreateCompanyMutation();
  const [updateCompany, statusUpdate] = useUpdateCompanyMutation();
  const [uploadCompanyImages, uploadState] = useUploadCompanyImagesMutation();
  const {
    data: bankAccounts,
    isLoading: bankAccountsLoading,
    isError: bankAccountsError,
    error: bankAccountsFetchError,
  } = useGetBankAccountsForCompanyQuery(createdCompanyId as number, {
    refetchOnMountOrArgChange: true,
    skip: createdCompanyId === null,
  });

  const status = createdCompanyId ? statusUpdate : statusCreate;

  const entityForm = useEntityForm<Company>({
    initialData: companyValues,
    fields: companyFields,
    redirectUrl: CompaniesRoutes.root,
    submitFn: (data) => {
      if (createdCompanyId) {
        return updateCompany({ data, id: createdCompanyId }).unwrap();
      } else {
        const { id, ...payload } = data;
        return createCompany(payload).unwrap();
      }
    },
    showSuccessBeforeRedirect: true,
    redirectDelay: 2000,
    onSuccessWithResponse: (response, shouldRedirect) => {
      if (!shouldRedirect && !createdCompanyId) {
        setCreatedCompanyId(response.companyId);
      }
    },
    mutationStatus: status,
    title: 'Dodaj firmę',
    icon: <InfoIcon />,
    backUrl: CompaniesRoutes.root,
    groupOrder: [
      FormRenderGroupTypes.basic,
      FormRenderGroupTypes.address,
      FormRenderGroupTypes.values,
    ],
    groupIcons: {
      Podstawowe: <InfoIcon sx={{ color: (theme) => theme.palette.primary.main }} />,
      Adres: <LocationOnIcon sx={{ color: (theme) => theme.palette.primary.main }} />,
    },
  });
  return (
    <EntityFormLayout {...entityForm}>
      <AccordionCard title="Logo" icon={<ImageIcon />}>
        {createdCompanyId ? (
          <UploadImagesForm
            useUploadMutation={() => [uploadCompanyImages, uploadState]}
            mutationParams={(files) => ({ companyId: createdCompanyId, files })}
          />
        ) : (
          <Typography color="text.secondary">
            Logo możesz dodać dopiero po zapisaniu firmy.
          </Typography>
        )}
      </AccordionCard>
      {createdCompanyId !== null && (
        <CompanyAccountsBankTable
          companyId={createdCompanyId}
          data={bankAccounts ?? []}
          isLoading={bankAccountsLoading}
          isError={bankAccountsError}
          error={bankAccountsFetchError}
        />
      )}
    </EntityFormLayout>
  );
};

export default CreateCompany;
